import { el, setStyle, damp, ease } from './util.js';

/**
 * Full-screen main menu overlay. Visible only while `game.phase === 'menu'`.
 * The live 3D world keeps rendering behind it — this is an in-engine main
 * menu (DEPLOY drops you straight into the running world), not a static
 * splash screen, so the scrim stays light (a diagonal gradient + a touch of
 * saturation/brightness) rather than a heavy blur.
 *
 * Driven on rawDt so the fade still runs while `time.scale === 0` before a
 * match has started. Defensive against a missing `game` subsystem (capture
 * harness / early boot): with no GameSystem present this renders fully
 * hidden, matching today's behaviour.
 */
export class TitleScreen {
  constructor(parent, ctx) {
    this.ctx = ctx;
    this.root = el('div', 'ow-title', parent);
    const inner = el('div', 'ow-title-inner', this.root);

    el('div', 'ow-title-eyebrow', inner, 'OVERWATCH // MARKET DISTRICT');
    el('h1', 'ow-title-h1', inner, 'CALL OF LUCAS');
    // Operation name chosen once per boot by index.html so the loading screen
    // and this menu always agree within a session.
    el('div', 'ow-title-op', inner, window.__SUBTITLE__ ?? 'NAP OPS');
    el('div', 'ow-title-rule', inner);
    el('div', 'ow-title-sub', inner, 'SOLO OPERATION — HOLD THE STREET');

    const btns = el('div', 'ow-title-btns', inner);
    this.deployBtn = el('button', 'ow-btn primary ow-title-deploy', btns, 'DEPLOY');
    this.deployBtn.type = 'button';
    this.deployBtn.addEventListener('click', () => {
      this.ctx.peek('game')?.startMatch?.();
    });

    el(
      'div',
      'ow-title-hint',
      inner,
      'WASD MOVE · MOUSE AIM · R RELOAD · SHIFT SPRINT · TAB SCORE · ESC PAUSE'
    );

    this.shown = 0;
    setStyle(this.root, 'display', 'none');
    setStyle(this.root, 'pointer-events', 'none');
  }

  /** Driven with unscaled time so the fade still runs before the match starts. */
  update(rawDt) {
    const game = this.ctx.peek('game');
    const phase = game?.phase ?? 'playing';
    const target = phase === 'menu' ? 1 : 0;

    this.shown = damp(this.shown, target, 10, rawDt);
    if (this.shown < 0.004) {
      setStyle(this.root, 'display', 'none');
      setStyle(this.root, 'pointer-events', 'none');
      return;
    }
    setStyle(this.root, 'display', '');
    setStyle(this.root, 'pointer-events', target > 0.5 ? 'auto' : 'none');
    setStyle(this.root, 'opacity', ease.outQuad(this.shown).toFixed(3));
  }

  dispose() {
    this.root.remove();
  }
}
